import classnames from 'classnames'
import * as React from 'react'

import { Icon } from './icon'

interface RedGreenTextProps {
  value?: number | null
  children: any
  rightContent?: React.ReactNode
  className?: string
  rightClass?: string
  min?: number
  max?: number
  direction?: 'higher-is-better' | 'lower-is-better'
}

/**
 * Component which renders red/green text based on whether the value is positive or negative if value
 * is 0, it will not be colored and it will not display the guide triangle
 */
export function RedGreenText({
  value,
  children,
  className,
  rightContent,
  rightClass,
  min,
  max,
  direction = 'higher-is-better',
}: RedGreenTextProps) {
  const hasValue = value && Number.isFinite(value)
  const showArrow = rightContent === undefined && hasValue && value !== 0

  const { isPositive, isNegative } = React.useMemo(() => {
    if (!hasValue) return { isPositive: false, isNegative: false }

    // If min/max are provided, use them with direction logic
    if (min !== undefined || max !== undefined) {
      if (direction === 'lower-is-better') {
        // For lower-is-better: only color if outside the range
        // Green if below min, red if above max, neutral in between
        return {
          isPositive: min !== undefined && value < min,
          isNegative: max !== undefined && value > max,
        }
      } else {
        // For higher-is-better: only color if outside the range
        // Green if above max, red if below min, neutral in between
        return {
          isPositive: max !== undefined && value > max,
          isNegative: min !== undefined && value < min,
        }
      }
    }

    // Default behavior: positive values are green, negative are red
    return {
      isPositive: value > 0,
      isNegative: value < 0,
    }
  }, [hasValue, value, min, max, direction])

  return (
    <span
      className={classnames(className, 'w-full items-baseline justify-end whitespace-nowrap', {
        'text-positive': hasValue && isPositive,
        'text-negative': hasValue && isNegative,
        'text-muted-2': typeof value !== 'number',
      })}
    >
      <span>{children}</span>
      <span className={classnames(rightClass, 'w-0 text-center', { 'pl-px': rightContent || showArrow })}>
        {showArrow ? (
          <Icon width={12} name={hasValue && value > 0 ? 'arrowUpShort' : 'arrowDownShort'} className="block" />
        ) : null}
        {rightContent}
      </span>
    </span>
  )
}

/**
 * Used to position multiple lines of RedGreen text, this component will make sure all numbers are
 * in line with their `rightContent`
 */
export function RedGreenMultiline({ className, children, ...props }: React.PropsWithChildren<any>) {
  return (
    <div {...props} className={classnames(className, 'table w-full')}>
      {React.Children.map(children, (child: React.ReactElement<RedGreenTextProps>) => {
        if (React.isValidElement(child) && child.type === RedGreenText) {
          return React.cloneElement(child, {
            className: classnames(child.props.className, 'table-row'),
            rightClass: 'table-cell align-middle',
          })
        }

        return child
      })}
    </div>
  )
}
